import React, { useState } from 'react';
import { useAutoFix } from '../../context/AutoFixContext';
import { CloudUpload, ExternalLink, CheckCircle2, Shield, Layers, FileCode } from 'lucide-react';

export const DeployPanel: React.FC = () => {
  const { activeIncident } = useAutoFix();
  const [isDeploying, setIsDeploying] = useState(false);
  const [deployUrl, setDeployUrl] = useState<string | null>(null);

  const handleDeploy = (e: React.FormEvent) => {
    e.preventDefault();
    setIsDeploying(true);

    setTimeout(() => {
      setIsDeploying(false);
      const previewId = Math.random().toString(36).substring(2, 7);
      setDeployUrl(`https://autofix-preview-${previewId}.netlify.app`);
    }, 1200);
  };

  const hasVerifiedPatch = activeIncident?.patch?.verified;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
        padding: 14,
        background: 'var(--bg-card)',
        borderRadius: 'var(--radius-md)',
        border: '1px solid var(--border)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div
            style={{
              width: 22,
              height: 22,
              borderRadius: 4,
              background: '#00C7B7',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#000',
              fontWeight: 800,
              fontSize: 11,
            }}
          >
            N
          </div>
          <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-1)' }}>
            Netlify Instant Deploy Preview (Edge Patch Rollout)
          </span>
        </div>

        <span
          className="badge"
          style={{ background: 'rgba(0, 199, 183, 0.1)', border: '1px solid #00C7B7', color: '#00C7B7', fontSize: 10 }}
        >
          NETLIFY READY
        </span>
      </div>

      <p style={{ fontSize: 12, color: 'var(--text-2)', lineHeight: 1.4, margin: 0 }}>
        When AutoFix.run verifies an in-browser hotpatch, it packages the AST diff and dispatches a
        declarative WebMCP tool to build a <strong>Netlify Deploy Preview</strong>.
      </p>

      {/* Declarative WebMCP Form Tool */}
      <form
        toolname="deployToNetlify"
        tooldescription="Deploys the verified self-healing patch to a Netlify Deploy Preview edge environment"
        onSubmit={handleDeploy}
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
          background: 'var(--bg-input)',
          padding: 12,
          borderRadius: 'var(--radius-sm)',
          border: '1px solid var(--border)',
        }}
      >
        <div style={{ fontSize: 11, fontFamily: 'var(--font-mono)', color: 'var(--text-3)' }}>
          &lt;form toolname="deployToNetlify"&gt;
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <div>
            <label style={{ fontSize: 10, color: 'var(--text-3)', display: 'block', marginBottom: 2 }}>
              Environment
            </label>
            <input
              type="text"
              name="environment"
              className="input"
              defaultValue="preview-edge"
              readOnly
              style={{ fontSize: 11, padding: '4px 8px' }}
            />
          </div>
          <div>
            <label style={{ fontSize: 10, color: 'var(--text-3)', display: 'block', marginBottom: 2 }}>
              Patch Target
            </label>
            <input
              type="text"
              name="patchId"
              className="input"
              value={activeIncident?.patch?.id || 'patch-staged-all'}
              readOnly
              style={{ fontSize: 11, padding: '4px 8px' }}
            />
          </div>
        </div>

        <button
          type="submit"
          className="btn btn-green"
          disabled={isDeploying}
          style={{
            marginTop: 4,
            padding: '8px',
            fontWeight: 600,
            fontSize: 12,
            background: hasVerifiedPatch ? 'var(--green-soft)' : 'rgba(0, 199, 183, 0.15)',
            borderColor: hasVerifiedPatch ? 'var(--green)' : '#00C7B7',
            color: hasVerifiedPatch ? 'var(--green)' : '#00C7B7',
          }}
        >
          <CloudUpload size={13} />
          {isDeploying
            ? 'Triggering Netlify Edge Build...'
            : 'Deploy Verified Patch to Netlify Edge'}
        </button>
      </form>

      {/* Generated Deploy URL */}
      {deployUrl && (
        <div
          className="animate-fade-in"
          style={{
            background: 'rgba(0, 199, 183, 0.08)',
            border: '1px solid #00C7B7',
            borderRadius: 'var(--radius-sm)',
            padding: '10px 12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <CheckCircle2 size={16} style={{ color: '#00C7B7' }} />
            <div>
              <div style={{ fontSize: 11, color: 'var(--text-3)' }}>Netlify Deploy Preview Active:</div>
              <a
                href={deployUrl}
                target="_blank"
                rel="noreferrer"
                style={{ fontSize: 12, color: '#00C7B7', fontWeight: 600, textDecoration: 'none' }}
              >
                {deployUrl}
              </a>
            </div>
          </div>

          <a
            href={deployUrl}
            target="_blank"
            rel="noreferrer"
            className="btn btn-sm"
            style={{ padding: '4px 8px', borderColor: '#00C7B7', color: '#00C7B7' }}
          >
            <ExternalLink size={11} /> Open
          </a>
        </div>
      )}
    </div>
  );
};
